import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Loader2, Check, Heart } from "lucide-react";

const INTERESTS = ["Parent", "School", "Partner", "Donor", "Volunteer"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", interest_area: "Parent", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Contact Us</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            Together, we can create a brighter future.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            For enrollment, partnerships, volunteering, or general inquiries — we'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <h2 className="font-display text-2xl font-black text-brandnavy">Connect with us</h2>
          <p className="mt-3 text-slate-600">Reach out directly or send us a message — whichever is easier for you.</p>
          <div className="mt-8 space-y-4">
            <a href="mailto:info@lpfoundation.world" data-testid="contact-email-link" className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-accent"><Mail className="h-6 w-6" /></span>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Email</p>
                <p className="font-semibold text-brandnavy">info@lpfoundation.world</p>
              </div>
            </a>
            <div className="rounded-2xl bg-brandnavy p-6 text-white">
              <Heart className="h-6 w-6 text-accent fill-current" />
              <p className="mt-3 font-display font-bold">Want to give instead?</p>
              <p className="mt-1 text-sm text-slate-300">Your donation directly funds programs for underserved youth.</p>
              <a href="/support" data-testid="contact-donate-link" className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-semibold transition hover:bg-orange-400">
                <Heart className="h-4 w-4 fill-current" /> Donate
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg">
            {done ? (
              <div data-testid="contact-success" className="py-12 text-center">
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600"><Check className="h-8 w-8" /></span>
                <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">Message sent!</h3>
                <p className="mt-2 text-slate-600">Thank you for reaching out. We'll be in touch as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name *"><input data-testid="contact-name" required value={form.name} onChange={update("name")} className={inputCls} /></Field>
                  <Field label="Email *"><input data-testid="contact-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} /></Field>
                  <Field label="Phone"><input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
                  <Field label="Organization"><input data-testid="contact-organization" value={form.organization} onChange={update("organization")} className={inputCls} /></Field>
                </div>
                <Field label="I am a... *">
                  <div className="flex flex-wrap gap-2" data-testid="contact-interest-group">
                    {INTERESTS.map((i) => (
                      <button
                        type="button"
                        key={i}
                        data-testid={`contact-interest-${i.toLowerCase()}`}
                        onClick={() => setForm({ ...form, interest_area: i })}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                          form.interest_area === i ? "bg-accent text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Message *">
                  <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={update("message")} className={inputCls} />
                </Field>
                <button data-testid="contact-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brandnavy px-6 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-accent/40 focus:border-accent";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
